import { useNavigate } from "react-router-dom";
import { ChevronRight, Trophy } from "lucide-react";
import { Panel, Empty } from "./Panel";
import { fmtHours } from "./format";

const risk = (days) => (days >= 14 ? { label: "High", c: "#E63946", bg: "#FBECEE" } : days >= 7 ? { label: "Watch", c: "#B8691E", bg: "#FCEFDE" } : { label: "OK", c: "#1E6F65", bg: "#E6F4F1" });

export function PendingByHolder({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Panel title="Pending work by holder" subtitle="Open files right now and how long the oldest has waited" testid="pending-holders" className="lg:col-span-6">
      {!rows.length ? <Empty text="Nothing pending — every file is closed." /> : (
        <div className="space-y-3">
          {rows.map((r) => {
            const rk = risk(r.oldest_days);
            return (
              <div key={r.id || r.name} data-testid={`pending-holder-${r.id}`} className="grid grid-cols-[minmax(0,1fr)_120px_64px] items-center gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold truncate">{r.name}</div>
                  <div className="h-1.5 bg-[#F0EFE7] mt-1.5"><div className="h-full bg-[#1D3557]" style={{ width: `${(r.count / max) * 100}%` }} /></div>
                </div>
                <div className="font-mono-eo text-[11px] text-[#5C5C54] text-right"><b className="text-[#0A0A0A] text-[13px]">{r.count}</b> open · {Math.floor(r.oldest_days)}d</div>
                <span className="text-[10px] font-mono-eo uppercase tracking-wider px-1.5 py-0.5 text-center border" style={{ color: rk.c, borderColor: rk.c, background: rk.bg }}>{rk.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

export function StuckFiles({ rows }) {
  const nav = useNavigate();
  return (
    <Panel title="Stuck files" subtitle="No action by the current holder for 7+ days" testid="stuck-files" className="lg:col-span-6">
      {!rows.length ? <Empty text="No stuck files. Movement is healthy." /> : (
        <div className="-mx-5 -my-5">
          {rows.map((r) => (
            <button
              key={r.id}
              data-testid={`stuck-file-${r.id}`}
              onClick={() => nav(`/files/${r.id}`)}
              className="w-full text-left px-5 py-3 border-b border-[#EFEEE5] last:border-b-0 flex items-center gap-3 transition-colors duration-150 hover:bg-[#FBFAF3]"
            >
              <div className="w-12 shrink-0 text-center">
                <div className="font-heading text-[20px] font-bold leading-none" style={{ color: r.days >= 14 ? "#E63946" : "#B8691E" }}>{Math.floor(r.days)}</div>
                <div className="font-mono-eo text-[9px] uppercase tracking-widest text-[#8A8A80]">days</div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold truncate">{r.name}</div>
                <div className="font-mono-eo text-[10.5px] text-[#8A8A80] truncate">with {r.holder_name}{r.department ? ` · ${r.department}` : ""}</div>
              </div>
              <ChevronRight size={15} className="text-[#8A8A80]" />
            </button>
          ))}
        </div>
      )}
    </Panel>
  );
}

export function StaffResponse({ rows }) {
  return (
    <Panel title="Staff response times" subtitle="On files you're involved in — time from receiving a file to opening it and passing it on" testid="staff-response" className="lg:col-span-12">
      {!rows.length ? <Empty text="No completed hand-offs in this period yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80] text-left border-b border-[#DCD9D0]">
                <th className="py-2 pr-3 w-10">#</th><th className="py-2 pr-3">Staff member</th>
                <th className="py-2 pr-3 text-right">Files handled</th><th className="py-2 pr-3 text-right">Avg time to open</th><th className="py-2 text-right">Avg time to act</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} data-testid={`staff-row-${r.id}`} className="border-b border-[#EFEEE5] last:border-b-0 transition-colors duration-150 hover:bg-[#FBFAF3]">
                  <td className="py-2.5 pr-3 font-mono-eo text-[#8A8A80]">{i === 0 ? <Trophy size={14} className="text-[#F4A261]" /> : i + 1}</td>
                  <td className="py-2.5 pr-3"><div className="font-semibold">{r.name}</div><div className="font-mono-eo text-[10.5px] text-[#8A8A80]">{r.designation}</div></td>
                  <td className="py-2.5 pr-3 text-right font-mono-eo">{r.handled}</td>
                  <td className="py-2.5 pr-3 text-right font-mono-eo">{fmtHours(r.avg_open_hours)}</td>
                  <td className="py-2.5 text-right font-mono-eo font-semibold text-[#1D3557]">{fmtHours(r.avg_act_hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
